import { Field } from "formik";
import { useState } from "react";
import { useSelector } from "react-redux";
import Select from 'react-select';
import pmsConstants from "../../pmsConstants";
import PatientAppointmentDetails from "./PatientDetails";
import PatientDependentDetails from "./PatientDependentDetails";

export default function AppointmentRequestForm({
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  values,
}) {
  const [appointmentFor, setAppointmentFor] = useState("self");
  
  // Get serviceTypes from Redux store (same as ServiceLocationComponent)
  const loginDetails = useSelector(state => state.loginDetails);
  const serviceTypes = loginDetails?.serviceTypes || [];

  const handleAppointmentForChange = (value) => {
    setAppointmentFor(value);
    setFieldValue("appointmentFor", value);
  };

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Appointment For */}
      <div>
        <label className="block text-sm font-semibold mb-2">
          Appointment for <span className="text-red-500">*</span>
        </label>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="appointmentFor"
              value="self"
              checked={appointmentFor === "self"}
              onChange={() => handleAppointmentForChange("self")}
              className="accent-teal-600"
            />
            Myself
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="appointmentFor"
              value="dependent"
              checked={appointmentFor === "dependent"}
              onChange={() => handleAppointmentForChange("dependent")}
              className="accent-teal-600"
            />
            Dependent / Someone
          </label>
        </div>
      </div>

      {/* Dependent Details */}
      {appointmentFor === "dependent" && (
        <>
          <PatientDependentDetails
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleGenderChange={(g) => setFieldValue("deptGender", g)}
            values={values}
          />
          <hr className="border-gray-300" />
        </>
      )}

      {/* Speciality and Symptoms Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Speciality */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            Choose Speciality <span className="text-red-500">*</span>
          </label>

          <Select
            name="apptServiceType"
            options={serviceTypes}
            value={serviceTypes.find(option => option.value === values?.apptServiceType) || null}
            onChange={(selectedOption) => {
              const value = selectedOption ? selectedOption.value : "";
              setFieldValue("apptServiceType", value);
            }}
            onBlur={() => handleBlur({ target: { name: "apptServiceType" } })}
            placeholder="Please select"
            className={`
              ${errors.apptServiceType && touched.apptServiceType
                ? 'border-red-500 rounded-md' 
                : ''
              }
            `}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: errors.apptServiceType && touched.apptServiceType
                  ? '#ef4444' 
                  : state.isFocused 
                    ? '#14b8a6' 
                    : '#d1d5db',
                '&:hover': {
                  borderColor: errors.apptServiceType && touched.apptServiceType
                    ? '#ef4444' 
                    : '#14b8a6',
                },
                boxShadow: state.isFocused 
                  ? '0 0 0 3px rgba(20, 184, 166, 0.1)' 
                  : 'none',
                borderRadius: '0.375rem',
              })
            }}
          />
          
          {errors.apptServiceType && touched.apptServiceType && (
            <p className="text-red-500 text-sm mt-1">
              {errors.apptServiceType}
            </p>
          )}
        </div>

        {/* Symptoms */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            Symptoms <span className="text-red-500">*</span>
          </label>

          <Field
            as="textarea"
            name="apptReason"
            rows="1"
            className={`
              w-full rounded-md border px-3 py-2
              focus:outline-none focus:ring-2 focus:ring-teal-500
              ${errors.apptReason && touched.apptReason
                ? "border-red-500"
                : "border-gray-300"}
            `}
          />

          {errors.apptReason && touched.apptReason && (
            <p className="text-red-500 text-sm mt-1">
              {errors.apptReason}
            </p>
          )}
        </div>
      </div>

      {/* Urgency */}
      <div className="max-w-sm">
        <label className="block text-sm font-semibold mb-2">
          Urgency <span className="text-red-500">*</span>
        </label>

        <Select
          name="apptUrgency"
          options={[
            { value: pmsConstants.URGENCY_TYPE_URGENT_STRING, label: pmsConstants.URGENCY_TYPE_URGENT_STRING },
            { value: pmsConstants.URGENCY_TYPE_INTERMEDIATE_URGENT_STRING, label: pmsConstants.URGENCY_TYPE_INTERMEDIATE_URGENT_STRING },
            { value: pmsConstants.URGENCY_TYPE_NON_URGENT_STRING, label: pmsConstants.URGENCY_TYPE_NON_URGENT_STRING }
          ]}
          value={{
            value: values?.apptUrgency,
            label: values?.apptUrgency
          }}
          onChange={(selectedOption) => {
            setFieldValue("apptUrgency", selectedOption.value);
          }}
          onBlur={() => handleBlur({ target: { name: "apptUrgency" } })}
          placeholder="Please select"
          className={`
            ${errors.apptUrgency && touched.apptUrgency
              ? 'border-red-500 rounded-md' 
              : ''
            }
          `}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: errors.apptUrgency && touched.apptUrgency
                ? '#ef4444' 
                : state.isFocused 
                  ? '#14b8a6' 
                  : '#d1d5db',
              '&:hover': {
                borderColor: errors.apptUrgency && touched.apptUrgency
                  ? '#ef4444' 
                  : '#14b8a6',
              },
              boxShadow: state.isFocused 
                ? '0 0 0 3px rgba(20, 184, 166, 0.1)' 
                : 'none',
              borderRadius: '0.375rem',
            })
          }}
        />
      </div>

      <hr className="border-gray-300" />

      {/* Appointment Details */}
      <PatientAppointmentDetails
        errors={errors}
        touched={touched}
        handleBlur={handleBlur}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        values={values}
        handleSearchRadiusChange={(value) =>
          setFieldValue("searchRadius", value)
        }
      />
    </div>
  );
}
